import { useEffect, useState } from "react";

export interface InputImageState {
  imageFile: File | null;
  preview: string | undefined;
  valid?: boolean;
  invalid?: boolean;
  feedbackValid?: string;
  feedbackInvalid?: string;
}

export interface InputImageController {
  setImage: (e: React.ChangeEvent<HTMLInputElement>) => void;
  resetImage?: (id?: string) => void;
  onFocus?: () => void;
  peek: () => InputImageState;
}
export interface InputImageProp {
  feedbackValid?: string;
  feedbackInvalid?: string;
  validated?: boolean;
  validator?: (
    extension?: string,
    size?: number,
  ) => {
    valid?: boolean;
    feedback?: string;
    noValidate?: boolean;
  } | void;
  onFocus?: () => void;
}

const useImageInput = (defaultImage: string, p?: InputImageProp) => {
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [preview, setPreview] = useState(defaultImage);
  const [valid, setValid] = useState<boolean>(false);
  const [invalid, setInvalid] = useState<boolean>(false);
  const [feedbackValid, setFeedbackValid] = useState<string | undefined>(
    p?.feedbackValid ?? "",
  );
  const [feedbackInvalid, setFeedbackInvalid] = useState<string | undefined>(
    p?.feedbackInvalid ?? undefined,
  );

  const validated = p?.validated ?? false;

  const setImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  };

  const resetImage = (id?: string) => {
    setImageFile(null);
    setPreview(defaultImage);
    if (id) {
      const element = document.getElementById(id);
      if (element) {
        (element as HTMLInputElement).value = "";
      }
    }
  };

  useEffect(() => {
    if (p?.validator) {
      const imageFileExtension = imageFile?.name.split(".").pop();
      const result = p.validator(imageFileExtension);
      if (result) {
        if (result.noValidate !== undefined) {
          if (result.noValidate) {
            setValid(false);
            setInvalid(false);
            return;
          }
        }
        if (result?.valid) {
          setValid(true);
          setInvalid(false);
          if (result.feedback) {
            setFeedbackValid(result.feedback);
          }
        } else {
          setValid(false);
          setInvalid(true);
          if (result?.feedback) {
            setFeedbackInvalid(result.feedback);
          }
        }
      }
    }
  }, [p, imageFile, preview]);

  const peek = () => {
    return {
      imageFile,
      preview,
      valid: valid,
      invalid: invalid,
      feedbackValid: feedbackValid,
      feedbackInvalid: feedbackInvalid,
    };
  };

  const onFocus = () => {
    if (p?.onFocus) {
      p.onFocus();
    }
  };

  return [
    {
      imageFile,
      preview,
      valid: validated ? valid : false,
      invalid: validated ? invalid : false,
      feedbackValid: feedbackValid,
      feedbackInvalid: feedbackInvalid,
    },
    {
      setImage,
      setImageFile,
      setPreview,
      resetImage,
      onFocus,
      peek,
      setValid,
      setInvalid,
      setFeedbackInvalid,
      setFeedbackValid,
    },
  ] as const;
};

export default useImageInput;
